import { useEffect, useRef } from 'react';
import { toast } from '../hooks/use-toast';
import { Bell, Check } from 'lucide-react';
import { Button } from './ui/button';

const ReminderNotifications = ({ habits, completions, onToggleCompletion }) => {
  const checkedRemindersRef = useRef(new Set());
  const lastCheckRef = useRef(null);

  useEffect(() => {
    // Load today's already shown reminders from localStorage
    const today = new Date().toISOString().split('T')[0];
    const stored = localStorage.getItem(`reminders_shown_${today}`);
    if (stored) {
      checkedRemindersRef.current = new Set(JSON.parse(stored));
    }

    const checkReminders = () => {
      const now = new Date();
      const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      const currentMinute = `${today}_${currentTime}`;
      
      // Only check once per minute
      if (lastCheckRef.current === currentMinute) return;
      lastCheckRef.current = currentMinute;

      const todayStr = now.toISOString().split('T')[0];

      habits.forEach(habit => {
        if (!habit.reminderTime) return;

        // Check if habit is already completed today
        const isCompleted = completions.some(
          c => c.habitId === habit.id && c.date === todayStr
        );
        if (isCompleted) return;

        // Get all reminder times for this habit
        const reminderTimes = getReminderTimes(habit);
        
        reminderTimes.forEach(reminderTime => {
          const reminderKey = `${habit.id}_${todayStr}_${reminderTime}`;
          
          if (reminderTime === currentTime && !checkedRemindersRef.current.has(reminderKey)) {
            // Mark as shown
            checkedRemindersRef.current.add(reminderKey);
            
            // Save to localStorage
            localStorage.setItem(
              `reminders_shown_${todayStr}`,
              JSON.stringify([...checkedRemindersRef.current])
            );

            // Show notification
            showReminderToast(habit, todayStr, reminderTime);
          }
        });
      });
    };

    const getReminderTimes = (habit) => {
      if (!habit.reminderTime) return [];
      
      const times = [];
      const [baseHour, baseMin] = habit.reminderTime.split(':').map(Number);
      
      // Default: once a day
      if (!habit.reminderFrequency || habit.reminderFrequency === 'once') {
        times.push(habit.reminderTime);
      }
      
      // Interval-based reminders
      else if (habit.reminderFrequency === 'interval' && habit.reminderInterval) {
        const interval = habit.reminderInterval;
        let currentHour = baseHour;
        
        // Generate reminders from base time until end of day
        while (currentHour < 24) {
          const timeStr = `${String(currentHour).padStart(2, '0')}:${String(baseMin).padStart(2, '0')}`;
          times.push(timeStr);
          currentHour += interval;
        }
      }
      
      return times;
    };

    const showReminderToast = (habit, dateStr, reminderTime) => {
      // Get which reminder number this is
      const allReminders = getReminderTimes(habit);
      const reminderIndex = allReminders.indexOf(reminderTime);
      const isFirstReminder = reminderIndex === 0;
      
      toast({
        title: (
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-amber-500" />
            <span>
              {isFirstReminder ? 'Lembrete: ' : 'Lembrete novamente: '}
              {habit.name}
            </span>
          </div>
        ),
        description: (
          <div className="space-y-3 mt-2">
            <p className="text-sm">
              {habit.target 
                ? `Meta: ${habit.target} ${habit.unit}`
                : 'Hora de completar este hábito!'}
            </p>
            {habit.reminderFrequency === 'interval' && (
              <p className="text-xs text-muted-foreground">
                {reminderIndex > 0 
                  ? `Este é o ${reminderIndex + 1}º lembrete de hoje`
                  : `Próximo lembrete em ${habit.reminderInterval}h`}
              </p>
            )}
            <Button
              size="sm"
              className="w-full"
              onClick={() => {
                onToggleCompletion(habit.id, new Date(dateStr), null);
                toast({
                  title: '✓ Hábito completado!',
                  description: `${habit.name} foi marcado como feito`,
                });
              }}
            >
              <Check className="w-4 h-4 mr-2" />
              Marcar como feito
            </Button>
          </div>
        ),
        duration: 10000, // 10 seconds
      });

      // Play a subtle sound (optional)
      try {
        const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZSBY');
        audio.volume = 0.3;
        audio.play().catch(() => {});
      } catch (e) {}
    };

    // Check immediately
    checkReminders();

    // Check every minute
    const interval = setInterval(checkReminders, 60000);

    return () => clearInterval(interval);
  }, [habits, completions, onToggleCompletion]);

  // Clean up old reminder records (keep only last 7 days)
  useEffect(() => {
    const cleanupOldReminders = () => {
      const keys = Object.keys(localStorage);
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      keys.forEach(key => {
        if (key.startsWith('reminders_shown_')) {
          const dateStr = key.replace('reminders_shown_', '');
          const date = new Date(dateStr);
          if (date < sevenDaysAgo) {
            localStorage.removeItem(key);
          }
        }
      });
    };

    cleanupOldReminders();
  }, []);

  return null; // This component doesn't render anything
};

export default ReminderNotifications;
